<template>
  <Card class="communication-setting-wrapper" headless>
    <div class="communication-setting">
      <div class="communication-setting__details">
        <div class="communication-setting__name">
          {{ communicationSetting.name }}
          <span v-if="isRequired()">- Required</span>
          <Tooltip v-if="isRequired()" placement="top">
            <template #trigger>
              <i class="fas fa-info-circle ml-1"></i>
            </template>
            <template #tooltip>
              <span>
                This is a critical system communication. It cannot be turned
                off.
              </span>
            </template>
          </Tooltip>
        </div>
        <div class="communication-setting__description">
          {{ communicationSetting.description }}
        </div>
      </div>
      <div class="communication-setting__toggles">
        <div
          v-for="format in communicationSetting.supported_formats"
          :key="format"
          class="format-setting"
        >
          <div class="format-setting__icon-wrapper">
            <i :class="formatConfig[format].icon"></i>
          </div>
          <div>{{ formatConfig[format].name }}</div>
          <div class="format-setting__switch">
            <Tooltip :disabled="!isDisabled(format)">
              <template #trigger>
                <ToggleSwitch
                  :modelValue="displayValue(format)"
                  :disabled="isDisabled(format)"
                   @update:modelValue="updateValue(format, $event)"
                />
              </template>
              <template #tooltip>
                <span>{{ disabledTooltip(format) }}</span>
              </template>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  </Card>
</template>

<script setup lang="ts">
import { ref } from "vue";

import Card from "@/components/Card.vue";
import ToggleSwitch from "@/components/ToggleSwitch.vue";
import Tooltip from "@/components/Tooltip.vue";

const formatConfig = {
  email: { name: "Email", icon: "fa-regular fa-envelope" },
  sms: { name: "Text", icon: "fa-regular fa-message-lines" },
  push: { name: "App notification", icon: "fa-regular fa-message-exclamation" }
};

const props = defineProps({
  communicationSetting: {
    type: Object,
    required: true
  },
  communicationSettings: {
    type: Object,
    required: true
  }
});

const currentValues = ref({
  sms: props.communicationSetting.sms,
  email: props.communicationSetting.email,
  push: props.communicationSetting.push
});

const emit = defineEmits(["change"]);

function updateValue(key, value) {
  currentValues.value[key] = value;
  emit("change", {
    category: props.communicationSetting.category,
    key: props.communicationSetting.key,
    enabled: isEnabled(),
    ...currentValues.value
  });
}

function isEnabled() {
  return props.communicationSetting.supported_formats.some((format) => {
    return currentValues.value[format];
  });
}

function isRequired() {
  return props.communicationSetting.required;
}

function isDisabled(format) {
  return props.communicationSettings.isDisabled({
    category: props.communicationSetting.category,
    key: props.communicationSetting.key,
    format
  });
}

function displayValue(format) {
  return props.communicationSettings.displayValue({
    category: props.communicationSetting.category,
    key: props.communicationSetting.key,
    format
  });
}

function disabledTooltip(format) {
  if (displayValue(format)) {
    return "Required";
  } else {
    return "Disabled by a higher level setting";
  }
}
</script>

<style lang="scss" scoped>
.communication-setting-wrapper {
  margin-bottom: 10px;
}
.communication-setting {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  &--required {
    margin-top: 13px;
  }

  &__note {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    background: #ffec99;
    padding: 4px 21px;
  }

  &__name {
    font-weight: 700;
  }

  &__toggles {
    width: 250px;
  }
}
.format-setting {
  display: flex;
  align-items: center;
  padding-bottom: 10px;

  &:last-of-type {
    padding-bottom: 0px;
  }

  &__icon-wrapper {
    display: flex;
    width: 24px;
    font-size: 16px;
  }

  &__switch {
    margin-left: auto;
  }
}
</style>
